import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Trash2, ShieldAlert } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addMonths,
  subMonths,
  addYears,
  subYears,
} from "date-fns";
import { useApp } from "../contexts/AppContext";
import {
  financeAPI,
  studentSubscriptionsAPI,
  studentsAPI,
  trainersAPI,
  permissionsAPI,
} from "../services/api";
import { usePermissions } from "../components/HasPermission";
import LoadingOverlay from "../components/ui/LoadingOverlay";
import TransactionModal from "../components/ui/TransactionModal";
import FinanceScope from "../components/finance/FinanceScope";
import FinanceHero from "../components/finance/FinanceHero";
import IncomeStatement from "../components/finance/IncomeStatement";
import TrainerSalaries from "../components/finance/TrainerSalaries";
import MemberPayments from "../components/finance/MemberPayments";
import FixedExpenses from "../components/finance/FixedExpenses";
import TransactionsList from "../components/finance/TransactionsList";
import FinanceDetailSheet from "../components/finance/FinanceDetailSheet";
import {
  fmt,
  monthLabel,
  monthShort,
  categoryLabel,
  groupByCategory,
  paymentMethodLabel,
  statusLabel,
  sumBy,
} from "../components/finance/financeUtils";

const ymKey = (value) => String(value || "").slice(0, 7); // "2026-07"

const cleanDescription = (raw) => {
  const d = raw || "";
  if (d.includes("subscription undefined") || d.includes("package ")) {
    return "Abonelik Ödemesi";
  }
  return d;
};

const FinancePage = () => {
  const { selectedOrganizationId, setSelectedOrganization, organizations, userRole } =
    useApp();
  const { hasPermission } = usePermissions();
  const canDeletePayments = hasPermission("delete:payments");

  const orgId = selectedOrganizationId || "all";

  /* ---------------- period state ---------------- */
  const [period, setPeriod] = useState("monthly"); // 'monthly' | 'yearly'
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));

  const ranges = useMemo(() => {
    const mStart = startOfMonth(monthDate);
    const mEnd = endOfMonth(monthDate);
    const yStart = startOfYear(monthDate);
    const yEnd = endOfYear(monthDate);
    const window = { start: startOfMonth(subMonths(monthDate, 5)), end: mEnd };
    const prevM = subMonths(monthDate, 1);
    const prevY = subYears(monthDate, 1);
    const f = (d) => format(d, "yyyy-MM-dd");
    return {
      month: { start: f(mStart), end: f(mEnd) },
      year: { start: f(yStart), end: f(yEnd) },
      window: { start: f(window.start), end: f(window.end) },
      prevMonth: { start: f(startOfMonth(prevM)), end: f(endOfMonth(prevM)) },
      prevYear: { start: f(startOfYear(prevY)), end: f(endOfYear(prevY)) },
    };
  }, [monthDate]);

  /* ---------------- data state ---------------- */
  const [yearTx, setYearTx] = useState([]);
  const [balances, setBalances] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [windowPayments, setWindowPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [userMap, setUserMap] = useState(new Map());
  const [prevMonthNet, setPrevMonthNet] = useState(null);
  const [prevYearNet, setPrevYearNet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  /* ---------------- ui state ---------------- */
  const [sheet, setSheet] = useState(null); // {type:'tx',tx} | {type:'cat',row,kind}
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [modalType, setModalType] = useState("income");
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    tx: null,
    loading: false,
  });

  const orgMap = useMemo(
    () => new Map((organizations || []).filter((o) => o.id !== "all").map((o) => [o.id, o.name])),
    [organizations],
  );

  /* Tracks the most recent fetch — older in-flight fetches discard their
     results so a slow early response can't overwrite fresh data. */
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    const myId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const safe = (p) => p.catch(() => null);
      const [
        yearTxRes,
        balancesRes,
        subsRes,
        paymentsRes,
        studentsRes,
        trainersRes,
        prevMonthSummaryRes,
        prevYearSummaryRes,
        managersRes,
        adminsRes,
      ] = await Promise.all([
        financeAPI.getTransactions(orgId, ranges.year.start, ranges.year.end),
        financeAPI.getTrainerBalances(
          orgId,
          period === "yearly" ? ranges.year.start : ranges.month.start,
          period === "yearly" ? ranges.year.end : ranges.month.end,
        ),
        studentSubscriptionsAPI.getAll(orgId),
        studentSubscriptionsAPI.getPayments(orgId, ranges.window.start, ranges.window.end),
        studentsAPI.getAll(orgId, 2000),
        trainersAPI.getAll(orgId, 2000),
        safe(financeAPI.getSummary(orgId, ranges.prevMonth.start, ranges.prevMonth.end)),
        safe(financeAPI.getSummary(orgId, ranges.prevYear.start, ranges.prevYear.end)),
        safe(permissionsAPI.getRoleUsers("role:organization_manager")),
        safe(permissionsAPI.getRoleUsers("role:super_admin")),
      ]);

      if (myId !== fetchIdRef.current) return;

      setYearTx(yearTxRes.data.data || []);
      setBalances(balancesRes.data.data || []);
      setSubscriptions(subsRes.data.data?.items || subsRes.data.data || []);
      setWindowPayments(paymentsRes.data.data || []);
      setStudents(studentsRes.data.data?.items || studentsRes.data.data || []);
      setTrainers(trainersRes.data.data?.items || trainersRes.data.data || []);
      setPrevMonthNet(prevMonthSummaryRes?.data?.data?.net ?? null);
      setPrevYearNet(prevYearSummaryRes?.data?.data?.net ?? null);

      // recordedBy → display name map (legacy rows without recordedByName)
      const map = new Map();
      [
        ...(managersRes?.data?.data?.items || managersRes?.data?.data || []),
        ...(adminsRes?.data?.data?.items || adminsRes?.data?.data || []),
      ].forEach((u) => {
        const name = u.name || u.username || u.email || "Personel";
        [u.id, u.username, u.email, u.sub].forEach((k) => k && map.set(k, name));
      });
      setUserMap(map);
      setForbidden(false);
    } catch (error) {
      if (myId !== fetchIdRef.current) return;
      console.error("Error fetching finance data:", error);
      if (error?.response?.status === 403) setForbidden(true);
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  }, [orgId, ranges, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------------- derived data ---------------- */
  const studentMap = useMemo(
    () => new Map(students.map((s) => [s.id, `${s.name} ${s.surname || ""}`.trim()])),
    [students],
  );
  const trainerNameMap = useMemo(() => {
    const m = new Map();
    trainers.forEach((t) => {
      m.set(t.id, `${t.name} ${t.surname || ""}`.trim());
      m.set(`${t.organizationId}_${t.id}`, `${t.name} ${t.surname || ""}`.trim());
    });
    return m;
  }, [trainers]);

  const enrichedTrainersForModal = useMemo(
    () =>
      trainers.map((t) => ({
        ...t,
        name: `${t.name} ${t.surname || ""}`.trim(),
        organizationName: orgMap.get(t.organizationId) || "Bilinmeyen Kurum",
      })),
    [trainers, orgMap],
  );

  const enrichTx = useCallback(
    (t) => {
      const desc = cleanDescription(t.description);
      const studentName = t.studentId ? studentMap.get(t.studentId) : null;
      const trainerName = t.trainerId
        ? trainerNameMap.get(`${t.organizationId}_${t.trainerId}`) ||
          trainerNameMap.get(t.trainerId)
        : null;
      let displayTitle = desc || categoryLabel(t.category);
      if (t.category === "student_payment" && studentName) {
        displayTitle = `${studentName} — ${desc || "Üye ödemesi"}`;
      } else if (t.category === "trainer_salary" && trainerName) {
        displayTitle = `Eğitmen maaşı — ${trainerName}`;
      }
      return {
        ...t,
        description: desc,
        displayTitle,
        orgName: orgMap.get(t.organizationId) || null,
        recordedByName:
          t.recordedByName ||
          userMap.get(t.recordedBy) ||
          userMap.get(t.created_by) ||
          "Sistem/Yönetici",
      };
    },
    [studentMap, trainerNameMap, orgMap, userMap],
  );

  const yearTxEnriched = useMemo(() => yearTx.map(enrichTx), [yearTx, enrichTx]);

  const monthKey = format(monthDate, "yyyy-MM");
  const monthTx = useMemo(
    () => yearTxEnriched.filter((t) => ymKey(t.transactionDate) === monthKey),
    [yearTxEnriched, monthKey],
  );

  const activeTx = useMemo(() => {
    const list = period === "yearly" ? yearTxEnriched : monthTx;
    return [...list].sort(
      (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate),
    );
  }, [period, yearTxEnriched, monthTx]);

  const monthIncome = useMemo(
    () => sumBy(monthTx.filter((t) => t.type === "income"), (t) => t.amount),
    [monthTx],
  );
  const monthExpense = useMemo(
    () => sumBy(monthTx.filter((t) => t.type === "outcome"), (t) => t.amount),
    [monthTx],
  );
  const yearIncome = useMemo(
    () => sumBy(yearTxEnriched.filter((t) => t.type === "income"), (t) => t.amount),
    [yearTxEnriched],
  );
  const yearExpense = useMemo(
    () => sumBy(yearTxEnriched.filter((t) => t.type === "outcome"), (t) => t.amount),
    [yearTxEnriched],
  );

  const heroIncome = period === "yearly" ? yearIncome : monthIncome;
  const heroExpense = period === "yearly" ? yearExpense : monthExpense;

  const marginMonthly = monthIncome > 0 ? Math.round(((monthIncome - monthExpense) / monthIncome) * 100) : null;
  const marginYearly = yearIncome > 0 ? Math.round(((yearIncome - yearExpense) / yearIncome) * 100) : null;

  const incomeRows = useMemo(
    () => groupByCategory(activeTx.filter((t) => t.type === "income")),
    [activeTx],
  );
  const expenseRows = useMemo(
    () => groupByCategory(activeTx.filter((t) => t.type === "outcome")),
    [activeTx],
  );

  /* Trainer balances → merged per trainer (multi-org distribution) */
  const mergedTrainers = useMemo(() => {
    const map = new Map();
    balances.forEach((b) => {
      const key = b.id;
      const cur =
        map.get(key) || {
          mergeKey: key,
          name: b.name,
          earned: 0,
          earnedPrivate: 0,
          earnedGroup: 0,
          revenue: 0,
          paid: 0,
          sessionCount: 0,
          orgSplit: [],
        };
      cur.earned += b.earned || 0;
      cur.earnedPrivate += b.earnedPrivate || 0;
      cur.earnedGroup += b.earnedGroup || 0;
      cur.revenue += b.revenue || 0;
      cur.paid += b.paid || 0;
      cur.sessionCount += b.sessionCount || 0;
      cur.orgSplit.push({
        orgId: b.organizationId,
        orgName: orgMap.get(b.organizationId) || "Bilinmeyen Kurum",
        earned: b.earned || 0,
        paid: b.paid || 0,
      });
      map.set(key, cur);
    });
    return [...map.values()]
      .map((t) => {
        const base = t.earned > 0 ? t.earned : t.paid;
        return {
          ...t,
          orgSplit: t.orgSplit
            .map((o) => ({
              ...o,
              share: base > 0 ? (t.earned > 0 ? o.earned : o.paid) / base : 0,
            }))
            .sort((a, b) => b.share - a.share),
        };
      })
      .sort((a, b) => b.earned - a.earned);
  }, [balances, orgMap]);

  /* Member payment grid (last 6 months ending at selected month) */
  const memberRows = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(monthDate, i);
      months.push({ key: format(d, "yyyy-MM"), date: d, label: monthShort(d) });
    }

    const paymentsByStudent = new Map();
    windowPayments.forEach((p) => {
      if (!p.studentId) return;
      const inner = paymentsByStudent.get(p.studentId) || new Map();
      const k = ymKey(p.transactionDate);
      inner.set(k, (inner.get(k) || 0) + (Number(p.amount) || 0));
      paymentsByStudent.set(p.studentId, inner);
    });

    const subsByStudent = new Map();
    subscriptions.forEach((s) => {
      const list = subsByStudent.get(s.studentId) || [];
      list.push(s);
      subsByStudent.set(s.studentId, list);
    });

    const studentOrgMap = new Map(students.map((s) => [s.id, s.organizationId]));
    const ids = new Set([...paymentsByStudent.keys(), ...subsByStudent.keys()]);
    const rows = [];
    ids.forEach((sid) => {
      const name = studentMap.get(sid);
      if (!name) return;
      const subs = (subsByStudent.get(sid) || []).sort(
        (a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0),
      );
      const activeSub =
        subs.find((s) => s.status === "active" || s.status === "ongoing") || subs[0];
      const payMap = paymentsByStudent.get(sid) || new Map();

      const covers = (mk) =>
        subs.some((s) => {
          const sk = ymKey(s.startDate);
          const ek = ymKey(s.endDate);
          if (!sk || !ek) return false;
          return sk <= mk && mk <= ek;
        });

      let latestPaidKey = null;
      months.forEach((m) => {
        if ((payMap.get(m.key) || 0) > 0) latestPaidKey = m.key;
      });

      const cells = months.map((m) => {
        const amount = payMap.get(m.key) || 0;
        let status = "none";
        let remaining = 0;
        if (amount > 0) {
          status = "paid";
          if (
            activeSub &&
            activeSub.paymentStatus === "partial" &&
            m.key === latestPaidKey
          ) {
            status = "part";
            remaining = Number(activeSub.remainingAmount) || 0;
          }
        } else if (covers(m.key)) {
          const sub = subs.find((s) => ymKey(s.startDate) <= m.key && m.key <= ymKey(s.endDate));
          status = sub && sub.paymentStatus !== "paid" ? "unpaid" : "none";
        }
        return { ...m, amount, status, remaining };
      });

      const currentCell = cells[cells.length - 1];
      rows.push({
        id: sid,
        name,
        packageName: activeSub?.packageName || null,
        orgName: orgMap.get(studentOrgMap.get(sid)) || null,
        months: cells,
        currentStatus: currentCell?.status || "none",
        unpaidWeight:
          (currentCell?.status === "unpaid" ? 2 : 0) +
          (currentCell?.status === "part" ? 1 : 0),
      });
    });

    return rows.sort(
      (a, b) => b.unpaidWeight - a.unpaidWeight || a.name.localeCompare(b.name, "tr-TR"),
    );
  }, [windowPayments, subscriptions, students, studentMap, orgMap, monthDate]);

  /* Fixed / recurring expense table rows (monthly vs yearly) */
  const expenseDetailRows = useMemo(() => {
    const map = new Map();
    yearTxEnriched
      .filter((t) => t.type === "outcome")
      .forEach((t) => {
        const key = t.category || "other";
        const cur = map.get(key) || { key, label: categoryLabel(key), monthly: 0, yearly: 0 };
        const amt = Number(t.amount) || 0;
        cur.yearly += amt;
        if (ymKey(t.transactionDate) === monthKey) cur.monthly += amt;
        map.set(key, cur);
      });
    return [...map.values()].sort((a, b) => b.yearly - a.yearly);
  }, [yearTxEnriched, monthKey]);

  /* ---------------- handlers ---------------- */
  const handleOrgChange = (newOrgId) => {
    if (newOrgId === "all") {
      setSelectedOrganization("All Organizations");
      return;
    }
    const org = (organizations || []).find((o) => o.id === newOrgId);
    if (org) setSelectedOrganization(org.name);
  };

  const handlePrev = () =>
    setMonthDate((d) => (period === "yearly" ? subYears(d, 1) : subMonths(d, 1)));
  const handleNext = () =>
    setMonthDate((d) => (period === "yearly" ? addYears(d, 1) : addMonths(d, 1)));

  const openAddModal = (type) => {
    setEditingTransaction(null);
    setModalType(type);
    setIsModalOpen(true);
  };

  const openEditModal = (tx) => {
    setSheet(null);
    setEditingTransaction(tx);
    setModalType(tx.category === "trainer_salary" ? "trainer_salary" : tx.type);
    setIsModalOpen(true);
  };

  const handleSubmitTransaction = async (formData) => {
    try {
      const dataToSave = { ...formData };
      if (modalType === "trainer_salary") {
        dataToSave.category = "trainer_salary";
        dataToSave.type = "outcome";
      }
      const effectiveOrgId = formData.organizationId || orgId;
      dataToSave.organizationId = effectiveOrgId;
      if (!effectiveOrgId || effectiveOrgId === "all") {
        alert("Lütfen kurum (organizasyon) seçin.");
        return;
      }
      if (editingTransaction) {
        await financeAPI.updateTransaction(effectiveOrgId, editingTransaction.id, dataToSave);
      } else {
        await financeAPI.createTransaction(effectiveOrgId, dataToSave);
      }
      setIsModalOpen(false);
      setEditingTransaction(null);
      fetchData();
    } catch (error) {
      console.error("Failed to save transaction:", error);
    }
  };

  const requestDelete = (tx) => {
    setSheet(null);
    setDeleteConfirm({ open: true, tx, loading: false });
  };

  const confirmDelete = async () => {
    const { tx } = deleteConfirm;
    if (!tx) return;
    setDeleteConfirm((prev) => ({ ...prev, loading: true }));
    try {
      if (
        tx.category === "student_payment" &&
        tx.subscriptionId &&
        tx.subscriptionId !== "demo"
      ) {
        await studentSubscriptionsAPI.deletePayment(orgId, tx.subscriptionId, tx.id);
      } else {
        await financeAPI.deletePayment(orgId, tx.id);
      }
      setDeleteConfirm({ open: false, tx: null, loading: false });
      fetchData();
    } catch (error) {
      console.error("Failed to delete transaction:", error);
      setDeleteConfirm((prev) => ({ ...prev, loading: false }));
    }
  };

  /* ---------------- sheet content ---------------- */
  const sheetProps = useMemo(() => {
    if (!sheet) return null;
    if (sheet.type === "tx") {
      const t = sheet.tx;
      const isIn = t.type === "income";
      return {
        title: t.displayTitle,
        sub: `${categoryLabel(t.category)}${t.orgName ? ` · ${t.orgName}` : ""}`,
        amount: `${isIn ? "+" : "−"}${fmt(t.amount)}`,
        amountColor: isIn ? "var(--fin-pos)" : "var(--fin-neg)",
        rows: [
          ["Tarih", t.transactionDate ? format(new Date(t.transactionDate), "dd/MM/yyyy") : "—"],
          ["Şube", t.orgName || "—"],
          ["Kategori", categoryLabel(t.category)],
          ["Ödeme yöntemi", paymentMethodLabel(t.paymentMethod)],
          ["Durum", statusLabel(t.status)],
          ["Kaydeden", t.recordedByName || "—"],
        ],
        onEdit: t.category === "student_payment" ? undefined : () => openEditModal(t),
        onDelete: canDeletePayments ? () => requestDelete(t) : undefined,
      };
    }
    // category row detail
    const { row, kind } = sheet;
    const rows = kind === "income" ? incomeRows : expenseRows;
    const total = rows.reduce((s, r) => s + r.amount, 0);
    const scopeNames =
      orgId === "all"
        ? [...orgMap.values()].join(" + ") || "Tüm şubeler"
        : orgMap.get(orgId) || "—";
    return {
      title: row.label,
      sub: `${kind === "income" ? "Gelir kalemi" : "Gider kalemi"} · ${
        period === "yearly" ? `${monthDate.getFullYear()} kümülatif` : monthLabel(monthDate)
      }`,
      amount: `${kind === "income" ? "+" : "−"}${fmt(row.amount)}`,
      amountColor: kind === "income" ? "var(--fin-pos)" : "var(--fin-neg)",
      rows: [
        ["Kapsam", scopeNames],
        ["Toplam içindeki payı", `%${total ? Math.round((row.amount / total) * 100) : 0}`],
        ["Kayıt sayısı", `${row.count} işlem`],
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, incomeRows, expenseRows, orgId, orgMap, period, monthDate, canDeletePayments]);

  /* ---------------- render ---------------- */
  const selectedOrgName = orgId === "all" ? null : orgMap.get(orgId);
  const brandText = `${selectedOrgName || "Konsolide"} · Finans`;
  const scopeNote =
    userRole === "organization_manager"
      ? "Yönetici görünümü: yalnızca yetkili olduğunuz şubenin verileri görünür."
      : userRole === "trainer"
        ? "Eğitmen görünümü: finans verilerine erişiminiz kısıtlıdır."
        : null;

  if (forbidden) {
    return (
      <div className="fin min-h-full bg-[var(--fin-paper)] flex items-center justify-center p-6">
        <div className="fin-card max-w-sm w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[var(--fin-warn-soft)] grid place-items-center">
            <ShieldAlert className="w-6 h-6 text-[var(--fin-warn)]" />
          </div>
          <h2 className="fin-grot text-[16px] font-semibold text-[var(--fin-text)] mb-1.5">
            Erişim kısıtlı
          </h2>
          <p className="text-[12.5px] text-[var(--fin-muted)] leading-relaxed">
            Finans verilerini görüntülemek için yönetici veya genel admin yetkisi
            gerekiyor.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fin min-h-full bg-[var(--fin-paper)]">
      <div className="max-w-[460px] xl:max-w-[1060px] mx-auto px-3 sm:px-4 pt-3 sm:pt-5 pb-16">
        <FinanceScope
          userRole={userRole}
          organizations={organizations}
          selectedOrgId={orgId}
          onOrgChange={handleOrgChange}
          note={scopeNote}
        />

        {/* Mobile: single column in artifact order (via `order-*` on
            display:contents wrappers). Desktop (xl): two columns. */}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:gap-5">
          <div className="contents xl:flex xl:flex-col xl:gap-5 xl:w-[46%] xl:shrink-0">
            <div className="order-1 xl:order-none">
              <FinanceHero
                brandText={brandText}
                period={period}
                monthDate={monthDate}
                onPrev={handlePrev}
                onNext={handleNext}
                onPeriodChange={setPeriod}
                income={heroIncome}
                expense={heroExpense}
                prevNet={period === "yearly" ? prevYearNet : prevMonthNet}
                prevLabel={period === "yearly" ? "geçen yıla" : "geçen aya"}
                prevSubLabel={
                  period === "monthly" && prevMonthNet != null
                    ? `${monthLabel(subMonths(monthDate, 1)).split(" ")[0]} net: ${fmt(prevMonthNet)}${prevMonthNet < 0 ? " −" : ""}`
                    : null
                }
                marginMonthly={marginMonthly}
                marginYearly={marginYearly}
              />
            </div>
            <div className="order-2 xl:order-none">
              <IncomeStatement
                incomeRows={incomeRows}
                expenseRows={expenseRows}
                onRowClick={(row, kind) => setSheet({ type: "cat", row, kind })}
              />
            </div>
            <div className="order-5 xl:order-none">
              <FixedExpenses
                rows={expenseDetailRows}
                yearShort={format(monthDate, "yy")}
              />
            </div>
          </div>

          <div className="contents xl:flex xl:flex-col xl:gap-5 xl:flex-1 xl:min-w-0">
            <div className="order-3 xl:order-none">
              <TrainerSalaries
                trainers={mergedTrainers}
                canAdd
                onAddSalary={() => openAddModal("trainer_salary")}
              />
            </div>
            <div className="order-4 xl:order-none">
              <MemberPayments members={memberRows} monthDate={monthDate} />
            </div>
            <div className="order-6 xl:order-none">
              <TransactionsList
                txs={activeTx}
                title={
                  period === "yearly"
                    ? `${monthDate.getFullYear()} · Tüm İşlemler`
                    : `${monthLabel(monthDate)} · Tüm İşlemler`
                }
                canAdd
                onAdd={() => openAddModal("income")}
                onRowClick={(tx) => setSheet({ type: "tx", tx })}
              />
            </div>
          </div>
        </div>

        <div className="text-center text-[11px] text-[var(--fin-muted)] mt-6 leading-relaxed">
          TheAthletics · Finans modülü
        </div>
      </div>

      {/* Detail bottom-sheet */}
      {sheetProps && (
        <FinanceDetailSheet open onClose={() => setSheet(null)} {...sheetProps} />
      )}

      {/* Add / edit transaction */}
      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTransaction(null);
        }}
        onSubmit={handleSubmitTransaction}
        transaction={editingTransaction}
        trainers={enrichedTrainersForModal}
        currentOrgId={orgId}
        organizations={organizations}
        title={
          editingTransaction
            ? editingTransaction.category === "trainer_salary"
              ? "Maaş Kaydını Düzenle"
              : "İşlemi Düzenle"
            : modalType === "trainer_salary"
              ? "Maaş Ekle"
              : "Yeni İşlem"
        }
      />

      {/* Delete confirm */}
      {deleteConfirm.open && (
        <div className="fin fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() =>
              !deleteConfirm.loading &&
              setDeleteConfirm({ open: false, tx: null, loading: false })
            }
          />
          <div className="relative bg-[var(--fin-card)] rounded-2xl shadow-2xl border border-[var(--fin-line)] w-full max-w-sm p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-[var(--fin-neg-soft)]">
              <Trash2 className="w-6 h-6 text-[var(--fin-neg)]" />
            </div>
            <h3 className="fin-grot text-center text-[15px] font-semibold text-[var(--fin-text)] mb-2">
              İşlemi Sil
            </h3>
            <p className="text-center text-[12px] text-[var(--fin-muted)] mb-1">
              Bu kaydı silmek istediğinize emin misiniz?
            </p>
            {deleteConfirm.tx && (
              <div className="mt-3 mb-5 bg-[var(--fin-hover)] rounded-xl p-3 border border-[var(--fin-line)] space-y-1">
                <p className="text-[11px] font-semibold text-[var(--fin-muted)] uppercase tracking-wide">
                  {deleteConfirm.tx.displayTitle}
                </p>
                <p className="fin-grot fin-num text-[15px] font-semibold text-[var(--fin-neg)]">
                  {fmt(deleteConfirm.tx.amount)}
                </p>
                {deleteConfirm.tx.category === "student_payment" && (
                  <p className="text-[10px] text-[var(--fin-muted)]">
                    ⚠️ Bu işlem aboneliğin ödeme durumunu günceller.
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ open: false, tx: null, loading: false })}
                disabled={deleteConfirm.loading}
                className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold bg-[var(--fin-chipbg)] border border-[var(--fin-line)] text-[var(--fin-text)] hover:bg-[var(--fin-hover)] transition-colors disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteConfirm.loading}
                className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold bg-[var(--fin-neg)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {deleteConfirm.loading ? "Siliniyor..." : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} />
    </div>
  );
};

export default FinancePage;
